import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bus, Calculator as CalcIcon, PlusCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatBRL } from '@/lib/format';

// Tipos de transporte com assentos padrão (editáveis)
const TRANSPORT_TYPES = [
  { id: 'onibus', label: 'Ônibus', seats: 46 },
  { id: 'micro', label: 'Micro-ônibus', seats: 26 },
  { id: 'van', label: 'Van', seats: 15 },
  { id: 'carro', label: 'Carro', seats: 4 },
];

const COST_FIELDS = [
  { key: 'transporte', label: 'Transporte (aluguel do veículo)' },
  { key: 'guia', label: 'Guia' },
  { key: 'insumos', label: 'Insumos' },
  { key: 'ingressos', label: 'Ingressos' },
  { key: 'hotel', label: 'Hotel' },
];

const EMPTY_COSTS = { transporte: '', guia: '', insumos: '', ingressos: '', hotel: '' };

const num = (v) => Number(v || 0);

export default function Calculator() {
  const navigate = useNavigate();
  const [transportType, setTransportType] = useState('onibus');
  const [seats, setSeats] = useState(46);
  const [ocupacaoMargem, setOcupacaoMargem] = useState(30); // % de assentos vazios (folga)
  const [margemLucro, setMargemLucro] = useState(30); // % sobre custo por passageiro
  const [costs, setCosts] = useState({ ...EMPTY_COSTS });
  const [nome, setNome] = useState('');
  const [destino, setDestino] = useState('');

  const onSelectType = (id) => {
    setTransportType(id);
    const t = TRANSPORT_TYPES.find((x) => x.id === id);
    if (t) setSeats(t.seats);
  };

  const setCost = (k) => (e) => setCosts({ ...costs, [k]: e.target.value });

  const resetAll = () => {
    setTransportType('onibus'); setSeats(46); setOcupacaoMargem(30);
    setMargemLucro(30); setCosts({ ...EMPTY_COSTS }); setNome(''); setDestino('');
  };

  const r = useMemo(() => {
    const seatsN = Math.max(0, Math.floor(num(seats)));
    const occ = Math.min(100, Math.max(0, num(ocupacaoMargem)));
    const assentosPagantes = Math.max(0, Math.floor(seatsN * (1 - occ / 100)));
    const custoTotal = COST_FIELDS.reduce((a, c) => a + num(costs[c.key]), 0);
    const custoPorPax = assentosPagantes > 0 ? custoTotal / assentosPagantes : 0;
    const lucro = Math.max(0, num(margemLucro));
    const precoVenda = custoPorPax * (1 + lucro / 100);
    const lucroPorPax = precoVenda - custoPorPax;
    const receitaTotal = precoVenda * assentosPagantes;
    const lucroTotal = lucroPorPax * assentosPagantes;
    return {
      seatsN, assentosPagantes, custoTotal, custoPorPax,
      precoVenda, lucroPorPax, receitaTotal, lucroTotal,
    };
  }, [seats, ocupacaoMargem, margemLucro, costs]);

  const typeLabel = TRANSPORT_TYPES.find((t) => t.id === transportType)?.label || 'Transporte';

  const addToTrips = () => {
    if (r.assentosPagantes <= 0) { toast.error('Defina os assentos e a margem de ocupação'); return; }
    if (r.custoTotal <= 0) { toast.error('Informe ao menos um custo da viagem'); return; }
    const custos = [
      { descricao: `Transporte (${typeLabel})`, valor: Math.round(num(costs.transporte) * 100) / 100, fornecedor: '' },
      { descricao: 'Guia', valor: Math.round(num(costs.guia) * 100) / 100, fornecedor: '' },
      { descricao: 'Insumos', valor: Math.round(num(costs.insumos) * 100) / 100, fornecedor: '' },
      { descricao: 'Ingressos', valor: Math.round(num(costs.ingressos) * 100) / 100, fornecedor: '' },
      { descricao: 'Hotel', valor: Math.round(num(costs.hotel) * 100) / 100, fornecedor: '' },
    ].filter((c) => c.valor > 0);
    const prefill = {
      nome: nome || '',
      destino: destino || '',
      preco_venda: Math.round(r.precoVenda * 100) / 100,
      quantidade_total: r.assentosPagantes,
      custos,
    };
    navigate('/erp/viagens', { state: { prefill } });
    toast.success('Dados enviados para o cadastro de viagem');
  };

  return (
    <div className="space-y-5" data-testid="calculator-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
            <CalcIcon className="h-6 w-6 text-primary" /> Calculadora de Precificação
          </h2>
          <p className="text-sm text-muted-foreground">Estime o preço de venda por passageiro para viagens próprias e envie direto para o cadastro.</p>
        </div>
        <Button variant="ghost" onClick={resetAll} data-testid="calc-reset"><RotateCcw className="h-4 w-4 mr-2" /> Limpar</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Inputs */}
        <Card className="p-5 lg:col-span-3 space-y-6">
          {/* Transporte */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold"><Bus className="h-4 w-4 text-primary" /> Transporte</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Tipo de transporte</Label>
                <Select value={transportType} onValueChange={onSelectType}>
                  <SelectTrigger className="mt-1.5" data-testid="calc-transport-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TRANSPORT_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label} ({t.seats} lug.)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assentos totais</Label>
                <Input type="number" min="0" value={seats} onChange={(e) => setSeats(e.target.value)} className="mt-1.5" data-testid="calc-seats" />
              </div>
              <div>
                <Label>Margem de ocupação (%)</Label>
                <Input type="number" min="0" max="100" value={ocupacaoMargem} onChange={(e) => setOcupacaoMargem(e.target.value)} className="mt-1.5" data-testid="calc-occupancy-margin" />
                <p className="text-[11px] text-muted-foreground mt-1">Assentos vazios de folga. Padrão: 30%.</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Custos */}
          <div className="space-y-4">
            <div className="text-sm font-semibold">Custos da viagem (valor total)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {COST_FIELDS.map((c) => (
                <div key={c.key}>
                  <Label>{c.label}</Label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                    <Input type="number" step="0.01" min="0" value={costs[c.key]} onChange={setCost(c.key)} placeholder="0,00" className="pl-9" data-testid={`calc-cost-${c.key}`} />
                  </div>
                </div>
              ))}
              <div>
                <Label>Margem de lucro (%)</Label>
                <Input type="number" min="0" value={margemLucro} onChange={(e) => setMargemLucro(e.target.value)} className="mt-1.5" data-testid="calc-profit-margin" />
              </div>
            </div>
          </div>

          <Separator />

          {/* Dados opcionais da viagem */}
          <div className="space-y-4">
            <div className="text-sm font-semibold">Dados da viagem (opcional — para o cadastro)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Nome da viagem</Label><Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex: Excursão Aparecida" className="mt-1.5" data-testid="calc-trip-name" /></div>
              <div><Label>Destino</Label><Input value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Ex: Aparecida - SP" className="mt-1.5" data-testid="calc-trip-destination" /></div>
            </div>
          </div>
        </Card>

        {/* Resultados */}
        <Card className="p-5 lg:col-span-2 space-y-4 h-fit lg:sticky lg:top-20">
          <div className="text-sm font-semibold">Resultado</div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Assentos pagantes</p>
              <p className="font-display text-2xl font-semibold tabular-nums" data-testid="calc-paying-seats">{r.assentosPagantes}</p>
              <p className="text-[11px] text-muted-foreground">de {r.seatsN} assentos</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Custo total</p>
              <p className="font-display text-2xl font-semibold tabular-nums" data-testid="calc-total-cost">{formatBRL(r.custoTotal)}</p>
            </div>
          </div>

          <div className="rounded-lg border p-3">
            <p className="text-xs text-muted-foreground">Custo por passageiro</p>
            <p className="font-display text-xl font-semibold tabular-nums" data-testid="calc-cost-per-pax">{formatBRL(r.custoPorPax)}</p>
          </div>

          <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-4">
            <p className="text-xs text-muted-foreground">Preço de venda sugerido / passageiro</p>
            <p className="font-display text-3xl font-bold text-primary tabular-nums" data-testid="calc-suggested-price">{formatBRL(r.precoVenda)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Lucro por passageiro: {formatBRL(r.lucroPorPax)}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Receita total</p>
              <p className="font-display text-lg font-semibold tabular-nums" data-testid="calc-total-revenue">{formatBRL(r.receitaTotal)}</p>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-xs text-muted-foreground">Lucro total</p>
              <p className="font-display text-lg font-semibold tabular-nums text-[hsl(174_55%_24%)]" data-testid="calc-total-profit">{formatBRL(r.lucroTotal)}</p>
            </div>
          </div>

          <Button className="w-full" onClick={addToTrips} data-testid="calc-add-to-trips">
            <PlusCircle className="h-4 w-4 mr-2" /> Adicionar ao cadastro de viagens
          </Button>
        </Card>
      </div>
    </div>
  );
}
